import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Check as CheckIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axiosConfig";
import { endpoints } from "../../api/endpoints";
import VehicleDocumentPreviewModal from "./Components/VehicleDocumentPreviewModal";
import { getApiErrorMessage } from "../../utils/apiError";
import { colors } from "../../theme/colors";
import UserProfileModal from "../../components/Models/UserProfileModal";

type CertificateStatus = "VALID" | "EXPIRING_SOON" | "EXPIRED" | "NOT_UPLOADED";

type CylinderDetail = {
  cylinderIndex: number;
  hydrotestDate: string;
  hydrotestDateValue: string;
  expiryDate: string;
  certificateStatus: CertificateStatus;
  certificateUploadedAt: string;
  certificateModifiedAt: string;
  certificatePhoto: string | null;
  certificatePhotoDownloadUrl: string | null;
};

type VehicleDetail = {
  id: string;
  registrationNumber: string;
  phoneNumber: string;
  numberOfCylinders: number;
  metalPlateDetails: string;
  certificatePresent: boolean;
  certificateStatus: CertificateStatus;
  metalPlatePhoto: string | null;
  metalPlatePhotoDownloadUrl: string | null;
  metalPlateUploadedAt: string;
  metalPlateModifiedAt: string;
  registeredDate: string;
  updatedAt: string;
  activeQrLinkedAt: string;
  cylinders: CylinderDetail[];
};

type VehicleDetailsApiCylinder = {
  cylinder_index?: number;
  hydrotest_date?: string;
  expiry_date?: string;
  certificate_status?: string;
  certificate_uploaded_at?: string;
  certificate_modified_at?: string;
  certificate_photo?: string;
  certificate_photo_download_url?: string;
};

type VehicleDetailsApiResponse = {
  id?: string;
  registration_number?: string;
  phone?: string;
  cylinder_count?: number;
  testing_verified?: boolean | null;
  certificate_present?: boolean;
  certificate_status?: string;
  metal_plate_photo?: string;
  metal_plate_photo_download_url?: string;
  metal_plate_uploaded_at?: string;
  metal_plate_modified_at?: string;
  cylinders?: VehicleDetailsApiCylinder[];
  created_at?: string;
  updated_at?: string;
  active_qr?: {
    qr_item_id?: string;
    linked_at?: string;
  };
};

const normalizeCertificateStatus = (value?: string): CertificateStatus => {
  switch ((value || "").toLowerCase().replace(/\s+/g, "_")) {
    case "valid":
      return "VALID";
    case "expired":
      return "EXPIRED";
    case "expiring_soon":
      return "EXPIRING_SOON";
    default:
      return "NOT_UPLOADED";
  }
};

const formatDate = (value?: string, withTime = false) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  }).format(date);
};

const formatDateInputValue = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMetalPlateDetails = (value?: boolean | null) => {
  if (value === true) {
    return "Readable";
  }

  if (value === false) {
    return "Not Readable";
  }

  return "--";
};

const normalizeVehicleDetails = (
  item: VehicleDetailsApiResponse,
): VehicleDetail => ({
  id: item.id || "--",
  registrationNumber: item.registration_number || "--",
  phoneNumber: item.phone || "--",
  numberOfCylinders: item.cylinder_count || 0,
  metalPlateDetails: getMetalPlateDetails(item.testing_verified),
  certificatePresent: Boolean(item.certificate_present),
  certificateStatus: item.certificate_present
    ? normalizeCertificateStatus(item.certificate_status)
    : "NOT_UPLOADED",
  metalPlatePhoto: item.metal_plate_photo || null,
  metalPlatePhotoDownloadUrl: item.metal_plate_photo_download_url || null,
  metalPlateUploadedAt: formatDate(item.metal_plate_uploaded_at, true),
  metalPlateModifiedAt: formatDate(item.metal_plate_modified_at, true),
  registeredDate: formatDate(item.created_at),
  updatedAt: formatDate(item.updated_at, true),
  activeQrLinkedAt: formatDate(item.active_qr?.linked_at, true),
  cylinders: Array.isArray(item.cylinders)
    ? item.cylinders.map((cylinder, index) => ({
        cylinderIndex: cylinder.cylinder_index || index + 1,
        hydrotestDate: formatDate(cylinder.hydrotest_date),
        hydrotestDateValue: formatDateInputValue(cylinder.hydrotest_date),
        expiryDate: formatDate(cylinder.expiry_date),
        certificateStatus: normalizeCertificateStatus(
          cylinder.certificate_status,
        ),
        certificateUploadedAt: formatDate(
          cylinder.certificate_uploaded_at,
          true,
        ),
        certificateModifiedAt: formatDate(
          cylinder.certificate_modified_at,
          true,
        ),
        certificatePhoto: cylinder.certificate_photo || null,
        certificatePhotoDownloadUrl:
          cylinder.certificate_photo_download_url || null,
      }))
    : [],
});

const cardSx = {
  borderRadius: "4px",
  border: "1px solid #D9D9D9",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
  bgcolor: "#FFFFFF",
};

const labelSx = {
  fontSize: "10px",
  color: "#6B7280",
  mb: 0.35,
  lineHeight: 1.2,
};

const valueSx = {
  fontSize: "14px",
  color: "#232323",
  lineHeight: 1.3,
};

const infoGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "repeat(8, minmax(0, 1fr))",
  },
  columnGap: { xs: 1.8, md: 2.3 },
  rowGap: 1.8,
  alignItems: "start",
};

const cylinderRowSx = {
  border: "1px solid #DCE5EF",
  borderRadius: "3px",
  px: 1.5,
  py: 1.15,
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "0.6fr 0.8fr 1fr 1fr 1.4fr 1.4fr",
  },
  columnGap: { xs: 1.6, md: 2.3 },
  rowGap: 1.1,
  mb: 1.4,
  bgcolor: "#FFFFFF",
};

const getStatusMeta = (status: CertificateStatus) => {
  switch (status) {
    case "VALID":
      return {
        label: "Valid",
        color: "#2E7D32",
        backgroundColor: "#E8F3E8",
        icon: <CheckIcon sx={{ fontSize: 14, color: "#2E7D32" }} />,
      };
    case "EXPIRING_SOON":
      return {
        label: "Expiring Soon",
        color: "#F97316",
        backgroundColor: "#FFF1E1",
        icon: <WarningAmberIcon sx={{ fontSize: 14, color: "#F97316" }} />,
      };
    case "EXPIRED":
      return {
        label: "Expired",
        color: "#E53935",
        backgroundColor: "#FBE4E8",
        icon: <WarningAmberIcon sx={{ fontSize: 14, color: "#E53935" }} />,
      };
    default:
      return {
        label: "Not Uploaded",
        color: "#666666",
        backgroundColor: "#F3F3F3",
        icon: <InfoIcon sx={{ fontSize: 14, color: "#666666" }} />,
      };
  }
};

type PreviewDocument = {
  title: string;
  previewUrl: string | null;
  downloadUrl: string | null;
  fileName: string;
  emptyTitle: string;
  emptyDescription: string;
};

const getSupportedDocumentExtension = (value?: string | null) => {
  if (!value) {
    return null;
  }

  let decodedValue = value;

  try {
    decodedValue = decodeURIComponent(value);
  } catch {
    decodedValue = value;
  }

  const normalizedValue = decodedValue.toLowerCase();

  if (
    normalizedValue.includes("application/pdf") ||
    /\.pdf(?:$|[?&";])/i.test(decodedValue)
  ) {
    return "pdf";
  }

  if (
    normalizedValue.includes("image/png") ||
    /\.png(?:$|[?&";])/i.test(decodedValue)
  ) {
    return "png";
  }

  if (
    normalizedValue.includes("image/jpeg") ||
    /\.jpe?g(?:$|[?&";])/i.test(decodedValue)
  ) {
    return normalizedValue.includes(".jpeg") ? "jpeg" : "jpg";
  }

  return null;
};

const buildPreviewFileName = (
  baseName: string,
  previewUrl?: string | null,
  downloadUrl?: string | null,
  fallbackExtension = "jpg",
) => {
  const extension =
    getSupportedDocumentExtension(previewUrl) ||
    getSupportedDocumentExtension(downloadUrl) ||
    fallbackExtension;

  return `${baseName}.${extension}`;
};

const VehicleDetails: React.FC = () => {
  const navigate = useNavigate();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const maxHydrotestDate = getTodayDateInputValue();
  const [activeCylinderIndex, setActiveCylinderIndex] = useState<number | null>(
    null,
  );
  const [selectedFiles, setSelectedFiles] = useState<
    Record<number, File | null>
  >({});
  const [selectedFileNames, setSelectedFileNames] = useState<
    Record<number, string>
  >({});
  const [editableHydrotestDates, setEditableHydrotestDates] = useState<
    Record<number, string>
  >({});
  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingCylinderIndexes, setUploadingCylinderIndexes] = useState<
    Record<number, boolean>
  >({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] =
    useState<PreviewDocument | null>(null);
  const activeRequestIdRef = useRef(0);

  const normalizedVehicleId = useMemo(() => {
    if (!vehicleId) {
      return "";
    }

    try {
      return decodeURIComponent(vehicleId);
    } catch {
      return vehicleId;
    }
  }, [vehicleId]);

  const fetchVehicleDetails = useCallback(
    async ({
      signal,
      clearSuccessMessage = true,
    }: {
      signal?: AbortSignal;
      clearSuccessMessage?: boolean;
    } = {}) => {
      const requestId = activeRequestIdRef.current + 1;

      activeRequestIdRef.current = requestId;
      setIsLoading(true);
      setErrorMessage(null);

      if (clearSuccessMessage) {
        setSuccessMessage(null);
      }

      try {
        const response = await api.post<VehicleDetailsApiResponse>(
          endpoints.vehicles.details,
          {
            vehicle_number: normalizedVehicleId,
            certificates: true,
          },
          {
            signal,
          },
        );

        const nextVehicle = normalizeVehicleDetails(response.data || {});

        setVehicle(nextVehicle);
        setSelectedFiles({});
        setSelectedFileNames({});
        setEditableHydrotestDates(
          nextVehicle.cylinders.reduce<Record<number, string>>(
            (acc, cylinder) => {
              acc[cylinder.cylinderIndex] = cylinder.hydrotestDateValue;
              return acc;
            },
            {},
          ),
        );

        return true;
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return false;
        }

        setErrorMessage(
          getApiErrorMessage(
            error,
            "Failed to load vehicle details. Please try again.",
          ),
        );
        setVehicle(null);
        setPreviewDocument(null);
        setSelectedFiles({});
        setSelectedFileNames({});
        setEditableHydrotestDates({});

        return false;
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [normalizedVehicleId],
  );

  useEffect(() => {
    if (!normalizedVehicleId) {
      setIsLoading(false);
      setSuccessMessage(null);
      setErrorMessage("Vehicle number is missing.");
      setVehicle(null);
      setPreviewDocument(null);
      setSelectedFiles({});
      setSelectedFileNames({});
      setEditableHydrotestDates({});
      return;
    }

    const controller = new AbortController();

    fetchVehicleDetails({ signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [fetchVehicleDetails, normalizedVehicleId]);

  const handleOpenMetalPlatePreview = () => {
    if (
      !vehicle ||
      !(vehicle.metalPlatePhoto || vehicle.metalPlatePhotoDownloadUrl)
    ) {
      return;
    }

    setPreviewDocument({
      title: `${vehicle.registrationNumber} - Metal Plate`,
      previewUrl: vehicle.metalPlatePhoto,
      downloadUrl: vehicle.metalPlatePhotoDownloadUrl,
      fileName: buildPreviewFileName(
        `${vehicle.registrationNumber}_metal_plate`,
        vehicle.metalPlatePhoto,
        vehicle.metalPlatePhotoDownloadUrl,
      ),
      emptyTitle: "Metal plate preview is not available for this vehicle.",
      emptyDescription:
        "Preview URL is not available or has expired for this metal plate image.",
    });
  };

  const handleOpenCertificatePreview = (cylinder: CylinderDetail) => {
    if (
      !vehicle ||
      !(cylinder.certificatePhoto || cylinder.certificatePhotoDownloadUrl)
    ) {
      return;
    }

    setPreviewDocument({
      title: `${vehicle.registrationNumber} - Cylinder ${cylinder.cylinderIndex} Hydro Certificate`,
      previewUrl: cylinder.certificatePhoto,
      downloadUrl: cylinder.certificatePhotoDownloadUrl,
      fileName: buildPreviewFileName(
        `${vehicle.registrationNumber}_certificate_cy${cylinder.cylinderIndex}`,
        cylinder.certificatePhoto,
        cylinder.certificatePhotoDownloadUrl,
      ),
      emptyTitle:
        "Hydro certificate preview is not available for this cylinder.",
      emptyDescription:
        "Preview URL is not available or has expired for this hydro certificate image.",
    });
  };

  const handleUploadCertificate = async (cylinder: CylinderDetail) => {
    const nextHydrotestDate =
      editableHydrotestDates[cylinder.cylinderIndex] ||
      cylinder.hydrotestDateValue;
    const selectedFile = selectedFiles[cylinder.cylinderIndex];
    const isHydrotestDateChanged =
      nextHydrotestDate !== cylinder.hydrotestDateValue;
    const hasFileChanged = Boolean(selectedFile);

    if (!vehicle || (!isHydrotestDateChanged && !hasFileChanged)) {
      return;
    }

    const formData = new FormData();

    formData.append("registration_number", vehicle.registrationNumber);

    if (isHydrotestDateChanged) {
      formData.append("hydrotest_date_indexes", String(cylinder.cylinderIndex));
      formData.append("hydrotest_dates", nextHydrotestDate);
    }

    if (selectedFile) {
      formData.append(
        "certificate_file_indexes",
        String(cylinder.cylinderIndex),
      );
      formData.append("certificate_files", selectedFile);
    }

    setUploadingCylinderIndexes((previous) => ({
      ...previous,
      [cylinder.cylinderIndex]: true,
    }));
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await api.put(endpoints.vehicles.hydroCertificates, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setEditableHydrotestDates((previous) => ({
        ...previous,
        [cylinder.cylinderIndex]: nextHydrotestDate,
      }));
      setSelectedFiles((previous) => ({
        ...previous,
        [cylinder.cylinderIndex]: null,
      }));
      setSelectedFileNames((previous) => ({
        ...previous,
        [cylinder.cylinderIndex]: "",
      }));
      setActiveCylinderIndex(null);

      const didRefreshVehicle = await fetchVehicleDetails({
        clearSuccessMessage: false,
      });

      if (didRefreshVehicle) {
        setSuccessMessage("Hydro certificate updated successfully.");
      }
    } catch (error: any) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "Failed to update hydro certificate. Please try again.",
        ),
      );
    } finally {
      setUploadingCylinderIndexes((previous) => ({
        ...previous,
        [cylinder.cylinderIndex]: false,
      }));
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#F5F5F5",
        minHeight: "100vh",
        // px: { xs: 2, md: 2.5 },
        // py: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          px: 3,
          py: 2,
          gap: 2,
          mb: 2.2,
        }}
      >
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.35 }}
          >
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
              sx={{
                minWidth: 0,
                p: 0,
                textTransform: "none",
                color: "#6B7280",
                fontSize: "12px",
                fontWeight: 500,
                "& .MuiButton-startIcon": {
                  mr: 0.25,
                },
              }}
            >
              Back
            </Button>

            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: colors.black,
                fontFamily: "Inter",
              }}
            >
              Vehicle Details -{" "}
              {vehicle?.registrationNumber || normalizedVehicleId || "--"}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>
            Admin Console / Registered Vehicles / Vehicle Details
          </Typography>
        </Box>

        <UserProfileModal />
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ px: 2.5, py: 1 }}>
        {isLoading ? (
          <Paper
            elevation={0}
            sx={{ ...cardSx, display: "flex", justifyContent: "center", py: 6 }}
          >
            <CircularProgress size={32} />
          </Paper>
        ) : !vehicle ? (
          <Paper elevation={0} sx={cardSx}>
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                Vehicle details are not available.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
            {(() => {
              const hasMetalPlatePreview = Boolean(
                vehicle.metalPlatePhoto || vehicle.metalPlatePhotoDownloadUrl,
              );

              return (
                <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                  <Box sx={{ px: 2.1, py: 1.9 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#232323",
                        mb: 2.1,
                      }}
                    >
                      Vehicle Information
                    </Typography>

                    <Box sx={infoGridSx}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>
                          Registration Number
                        </Typography>
                        <Typography
                          sx={{ ...valueSx, color: "#2E7D32", fontWeight: 500 }}
                        >
                          {vehicle.registrationNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>Phone Number</Typography>
                        <Typography sx={valueSx}>
                          {vehicle.phoneNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>
                          Number of Cylinders
                        </Typography>
                        <Typography sx={valueSx}>
                          {vehicle.numberOfCylinders}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>
                          Metal Plate Details
                        </Typography>
                        <Typography sx={valueSx}>
                          {vehicle.metalPlateDetails}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>Registered Date</Typography>
                        <Typography sx={valueSx}>
                          {vehicle.registeredDate}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>
                          Certificate Availability
                        </Typography>
                        <Typography sx={valueSx}>
                          {vehicle.certificatePresent ? "Yes" : "No"}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>Last Updated</Typography>
                        <Typography sx={valueSx}>
                          {vehicle.updatedAt}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={labelSx}>
                          Active QR Linked At
                        </Typography>
                        <Typography sx={valueSx}>
                          {vehicle.activeQrLinkedAt}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mt: 2.1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                        onClick={handleOpenMetalPlatePreview}
                        disabled={!hasMetalPlatePreview}
                        sx={{
                          minWidth: 146,
                          height: 32,
                          borderRadius: "2px",
                          textTransform: "none",
                          borderColor: "#81C784",
                          color: "#2E7D32",
                          fontSize: "11px",
                          fontWeight: 500,
                          px: 1.4,
                          "&:hover": {
                            borderColor: hasMetalPlatePreview
                              ? "#2E7D32"
                              : "#D1D5DB",
                            bgcolor: hasMetalPlatePreview
                              ? "#F3FBF3"
                              : "#F3F4F6",
                          },
                          "&.Mui-disabled": {
                            borderColor: "#D1D5DB",
                            color: "#9CA3AF",
                            bgcolor: "#F3F4F6",
                          },
                        }}
                      >
                        View Metal Plate
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              );
            })()}

            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ px: 2.1, py: 1.9 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.9,
                    mb: 1.7,
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 700, color: "#232323" }}
                  >
                    Hydro Certificate
                  </Typography>
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0];

                    if (activeCylinderIndex === null) {
                      return;
                    }

                    setSelectedFiles((previous) => ({
                      ...previous,
                      [activeCylinderIndex]: nextFile || null,
                    }));

                    setSelectedFileNames((previous) => ({
                      ...previous,
                      [activeCylinderIndex]: nextFile?.name || "",
                    }));

                    event.target.value = "";
                  }}
                />

                {vehicle.cylinders.length > 0 ? (
                  vehicle.cylinders.map((cylinder) => {
                    const statusMeta = getStatusMeta(
                      cylinder.certificateStatus,
                    );
                    const editableHydrotestDate =
                      editableHydrotestDates[cylinder.cylinderIndex] ||
                      cylinder.hydrotestDateValue;
                    const hydrotestDateLabel = editableHydrotestDate
                      ? formatDate(editableHydrotestDate)
                      : cylinder.hydrotestDate;
                    const isHydrotestDateChanged =
                      editableHydrotestDate !== cylinder.hydrotestDateValue;
                    const hasFileChanged = Boolean(
                      selectedFiles[cylinder.cylinderIndex],
                    );
                    const isUploadEnabled =
                      isHydrotestDateChanged || hasFileChanged;
                    const isUploading = Boolean(
                      uploadingCylinderIndexes[cylinder.cylinderIndex],
                    );
                    const hasCertificatePreview = Boolean(
                      cylinder.certificatePhoto ||
                      cylinder.certificatePhotoDownloadUrl,
                    );

                    return (
                      <Box key={cylinder.cylinderIndex} sx={cylinderRowSx}>
                        <Box>
                          <Typography sx={labelSx}>Cylinder</Typography>
                          <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                            {cylinder.cylinderIndex}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={labelSx}>Status</Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.4,
                            }}
                          >
                            {statusMeta.icon}
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: statusMeta.color,
                                lineHeight: 1.2,
                              }}
                            >
                              {statusMeta.label}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography sx={labelSx}>Hydrotest Date</Typography>
                          <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                            {hydrotestDateLabel}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={labelSx}>Expiry Date</Typography>
                          <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                            {cylinder.expiryDate}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={labelSx}>Uploaded At</Typography>
                          <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                            {cylinder.certificateUploadedAt}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={labelSx}>Modified At</Typography>
                          <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                            {cylinder.certificateModifiedAt}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            mt: 0.25,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2.1,
                              mb: 1.35,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.45,
                              }}
                            >
                              <InfoIcon
                                sx={{ fontSize: 13, color: "#6B7280" }}
                              />
                              <Typography
                                sx={{ fontSize: "12px", color: "#6B7280" }}
                              >
                                Cylinder Last Hydrotest Date
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.55,
                                flexWrap: "wrap",
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 14, color: "#232323" }}
                              />
                              <TextField
                                type="date"
                                size="small"
                                value={editableHydrotestDate}
                                slotProps={{
                                  htmlInput: { max: maxHydrotestDate },
                                }}
                                onChange={(event) => {
                                  setEditableHydrotestDates((previous) => ({
                                    ...previous,
                                    [cylinder.cylinderIndex]:
                                      event.target.value,
                                  }));
                                }}
                                sx={{
                                  width: 170,
                                  "& .MuiInputBase-root": {
                                    height: 30,
                                    bgcolor: "#FFFFFF",
                                  },
                                  "& .MuiInputBase-input": {
                                    fontSize: "12px",
                                    color: "#232323",
                                    px: 1,
                                    py: 0.55,
                                  },
                                }}
                              />
                            </Box>
                          </Box>

                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#232323",
                              mb: 1.1,
                            }}
                          >
                            Update Certificate
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.95,
                            }}
                          >
                            <Button
                              variant="outlined"
                              startIcon={
                                <VisibilityIcon sx={{ fontSize: 14 }} />
                              }
                              onClick={() => {
                                handleOpenCertificatePreview(cylinder);
                              }}
                              disabled={!hasCertificatePreview}
                              sx={{
                                minWidth: 132,
                                height: 30,
                                borderRadius: "2px",
                                textTransform: "none",
                                borderColor: "#81C784",
                                color: "#2E7D32",
                                fontSize: "10px",
                                fontWeight: 500,
                                px: 1.2,
                                "&:hover": {
                                  borderColor: hasCertificatePreview
                                    ? "#2E7D32"
                                    : "#D1D5DB",
                                  bgcolor: hasCertificatePreview
                                    ? "#F3FBF3"
                                    : "#F3F4F6",
                                },
                                "&.Mui-disabled": {
                                  borderColor: "#D1D5DB",
                                  color: "#9CA3AF",
                                  bgcolor: "#F3F4F6",
                                },
                              }}
                            >
                              View Certificate
                            </Button>

                            {selectedFileNames[cylinder.cylinderIndex] ? (
                              <Box
                                sx={{
                                  minHeight: 30,
                                  px: 1.2,
                                  borderRadius: "2px",
                                  border: "1px solid #81C784",
                                  bgcolor: "#F8FFF8",
                                  display: "flex",
                                  alignItems: "center",
                                  maxWidth: 220,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "10px",
                                    fontWeight: 500,
                                    color: "#2E7D32",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {selectedFileNames[cylinder.cylinderIndex]}
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                                onClick={() => {
                                  setActiveCylinderIndex(
                                    cylinder.cylinderIndex,
                                  );
                                  fileInputRef.current?.click();
                                }}
                                sx={{
                                  minWidth: 104,
                                  height: 30,
                                  borderRadius: "2px",
                                  textTransform: "none",
                                  borderColor: "#81C784",
                                  color: "#2E7D32",
                                  fontSize: "10px",
                                  fontWeight: 500,
                                  px: 1.2,
                                  "&:hover": {
                                    borderColor: "#2E7D32",
                                    bgcolor: "#F3FBF3",
                                  },
                                }}
                              >
                                Choose File
                              </Button>
                            )}

                            <Button
                              variant="contained"
                              startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                              disabled={!isUploadEnabled || isUploading}
                              onClick={() => {
                                handleUploadCertificate(cylinder);
                              }}
                              sx={{
                                minWidth: 132,
                                height: 30,
                                borderRadius: "2px",
                                textTransform: "none",
                                fontSize: "10px",
                                fontWeight: 500,
                                px: 1.2,
                                bgcolor: isUploadEnabled
                                  ? "#2E7D32"
                                  : "#E5E7EB",
                                color: isUploadEnabled ? "#FFFFFF" : "#A3A3A3",
                                boxShadow: "none",
                                "&:hover": {
                                  bgcolor: isUploadEnabled
                                    ? "#27642A"
                                    : "#E5E7EB",
                                  boxShadow: "none",
                                },
                                "&.Mui-disabled": {
                                  bgcolor: "#E5E7EB",
                                  color: "#A3A3A3",
                                },
                              }}
                            >
                              {isUploading
                                ? "Uploading..."
                                : "Upload Certificate"}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      ...cylinderRowSx,
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr",
                      },
                      mb: 1.8,
                    }}
                  >
                    <Box>
                      <Typography sx={labelSx}>Status</Typography>
                      <Typography sx={{ ...valueSx, fontSize: "12px" }}>
                        No cylinder certificate data available.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </>
        )}
      </Box>

      <VehicleDocumentPreviewModal
        open={Boolean(previewDocument)}
        onClose={() => setPreviewDocument(null)}
        title={previewDocument?.title || "Document Preview"}
        previewUrl={previewDocument?.previewUrl || null}
        downloadUrl={previewDocument?.downloadUrl || null}
        fileName={previewDocument?.fileName || "document.jpg"}
        emptyTitle={previewDocument?.emptyTitle}
        emptyDescription={previewDocument?.emptyDescription}
      />
    </Box>
  );
};

export default VehicleDetails;
